import {
    TCurrentTemplate,
    ICurrentConversation,
} from '../components/modalTemplates/types';
import {
    getAreaByWsp,
    getConversationFromNumber,
} from 'src/utils/areas/getDataAreas';
import {
    API_TWILIO,
    API_WSP,
    OUTCOME_DIRECTION,
} from 'src/constants/conversations';
import { TWILIO_TEMPLATE_VERSIONS } from 'src/modules/models/templateTwilio/constants';
import { IUser } from 'src/interfaces/user';
import { WHATSAPP_CHANNEL } from 'src/constants/conversations';

/**
 * Base configuration for template sending
 * Contains common logic shared between Lead and Conversation
 */
interface IBaseTemplateConfig {
    _id: string;
    to: string | null;
    from: string | null;
    body: string;
    contentSid?: string;
    by: { name: string; id: string };
    area: string;
    storeId?: string;
    channel: string;
    templateType: string;
    messageWithoutFormat: string;
    options: any;
    phone: string;
    templateTitle?: string;
    api: string;
    contentTemplateBuilder?: {
        contentSid: string;
        contentVariablesType: string;
    };
    language?: string;
}

/**
 * Factory to create configurations for sending templates
 * Uses a base configuration and extends it for specific contexts
 * @param {TCurrentTemplate} TCurrentTemplate
 * @param {ICurrentConversation} ICurrentConversation
 * @param {IUser} IUser
 * @returns {IBaseTemplateConfig} IBaseTemplateConfig
 * @example
 * const template = TemplateSendFactory.createBaseConfig(template, conversation, user);
 * const template = TemplateSendFactory.createForConversation(template, conversation, user);
 * const template = TemplateSendFactory.createForLead(template, conversation, user);
 */
export class TemplateSendFactory {
    static createBaseConfig(
        template: TCurrentTemplate,
        conversation: ICurrentConversation,
        user: IUser
    ): IBaseTemplateConfig {
        const { area = '', store, phone } = conversation;
        const { areas, _id: storeId } = store || {};

        const response = getAreaByWsp({ area, areas });
        const { api, whatsapp: whatsAppNumber } = response;

        let from = null;
        if (api === API_TWILIO) {
            from = getConversationFromNumber({
                channel: WHATSAPP_CHANNEL,
                area,
                areas,
            });
        }
        if (api === API_WSP) from = whatsAppNumber;

        let templateType = template.templateType;
        if (api === API_WSP) {
            templateType = TWILIO_TEMPLATE_VERSIONS.META_TEMPLATE;
        }

        return {
            _id: template._id,
            to: null,
            from,
            body: template.description,
            contentSid: template?.contentTemplateBuilder?.contentSid,
            by: { name: user.name, id: user._id },
            area,
            storeId,
            channel: WHATSAPP_CHANNEL,
            templateType,
            messageWithoutFormat: template.messageWithoutFormat,
            options: template.options,
            phone,
            templateTitle: template.templateTitle,
            api,
            contentTemplateBuilder: template?.contentTemplateBuilder,
            language: template?.language,
        };
    }

    /**
     * Creates configuration for Conversation context
     * Adds conversation-specific logic
     */
    static createForConversation(
        template: TCurrentTemplate,
        conversation: ICurrentConversation,
        user: IUser
    ) {
        const baseConfig = this.createBaseConfig(template, conversation, user);

        const {
            channel,
            direction,
            to: toConversation,
            from: fromConversation,
        } = conversation;

        let to = null;
        if (direction === OUTCOME_DIRECTION) {
            to = toConversation;
        } else {
            to = fromConversation;
        }

        return {
            ...baseConfig,
            to,
            channel,
        };
    }

    /**
     * Creates configuration for Lead context
     * Adds lead-specific logic
     */
    static createForLead(
        template: TCurrentTemplate,
        conversation: ICurrentConversation,
        user: IUser
    ) {
        const baseConfig = this.createBaseConfig(template, conversation, user);

        return {
            ...baseConfig,
            to: conversation.phone,
            message: template.description,
            title: template.title,
        };
    }
}
